import type { RoomObservingSession, SketchingSessionRoom } from './visit-counsel.types';

/**
 * displayLabel 이 "1번1번 방" 처럼 ordinal prefix 가 두 번 붙은 형태인지 검사한다.
 *
 * 과거 house-sketch flow 매퍼 버그로 namePrefix 에 "1번 방" 같은 ordinal 이 새어
 * 들어간 적이 있고, backend 가 displayLabel 을 `${ordinal}번${namePrefix}` 로
 * 만들면서 "1번1번 방" 형태가 굳어진 데이터가 존재한다. 새 데이터는 매퍼에서
 * 막혔지만 기존 sketching session 은 그대로 남아 있어 표시 단에서 한 번 더 정정한다.
 */
function isMalformedDisplayLabel(label: string): boolean {
  return /^\d+번\d+번/.test(label);
}

/** namePrefix 가 "1번 방" 처럼 ordinal 이 새어들어 있으면 베이스 한국어만 추출. */
function stripLeadingOrdinal(prefix: string): string {
  const stripped = prefix.replace(/^\d+번\s*/, '').trim();
  return stripped || prefix.trim();
}

/**
 * 라벨 포맷 옵션.
 */
export interface FormatSketchRoomLabelOptions {
  /**
   * `true` 이면 같은 type 의 룸이 1개뿐이라는 뜻 — ordinal prefix("1번"·"4번")
   * 를 떼고 베이스 한국어("거실"·"다락방") 만 노출한다.
   *
   * 단독일 때 prefix 가 붙는 게 비정상이라는 사용자 정책: ROOM 등 같은 type 이
   * 여러 개 있을 때만 "n번 X" 식 ordering 이 의미가 있다.
   *
   * `false`/`undefined` (기본값) 이면 기존 동작 — `displayLabel` 우선, 없으면
   * `${ordinalWithinType ?? index}번 ${namePrefix}` 합성.
   *
   * 같은 type 인지 판정은 호출자가 책임진다 (`isSingletonRoomType` helper 참고).
   */
  singleton?: boolean;
}

/**
 * 스케치 방 표시명 — API `displayLabel` 우선, 없으면 타입 내 순번(ordinalWithinType) + namePrefix.
 * 전역 index는 하위 호환 폴백으로만 사용한다.
 *
 * displayLabel 이 ordinal 이 두 번 붙은 형태이거나, namePrefix 자체에 ordinal 이
 * 새어들어 있는 경우(과거 매퍼 버그 데이터)에도 한 번만 prefix 가 붙도록 정정한다.
 *
 * `options.singleton === true` 인 경우 ordinal prefix 자체를 떼고 베이스 한국어만
 * 반환한다 (예: backend 가 "4번 다락방" 으로 보내도 단독 다락방이면 "다락방" 노출).
 */
export function formatSketchingRoomLabelSpaced(
  room: SketchingSessionRoom,
  options?: FormatSketchRoomLabelOptions
): string {
  const label = room.displayLabel?.trim();
  const usable = label && !isMalformedDisplayLabel(label) ? label : null;
  if (options?.singleton) {
    if (usable) return stripLeadingOrdinal(usable);
    return stripLeadingOrdinal(room.namePrefix);
  }
  if (usable) return usable;
  const n = room.ordinalWithinType ?? room.index;
  const prefix = stripLeadingOrdinal(room.namePrefix);
  return `${n}번 ${prefix}`;
}

/** 체크리스트 리프 등 공백 없는 조합 (`1번방`) */
export function formatSketchingRoomLabelCompact(
  room: SketchingSessionRoom,
  options?: FormatSketchRoomLabelOptions
): string {
  const label = room.displayLabel?.trim();
  const usable = label && !isMalformedDisplayLabel(label) ? label : null;
  if (options?.singleton) {
    if (usable) return stripLeadingOrdinal(usable);
    return stripLeadingOrdinal(room.namePrefix);
  }
  if (usable) return usable;
  const n = room.ordinalWithinType ?? room.index;
  const prefix = stripLeadingOrdinal(room.namePrefix);
  return `${n}번${prefix}`;
}

/**
 * roomObserving 표시명 — 세션의 displayLabel 우선, 없으면 sketching.rooms에서 roomId로 조인.
 * 둘 다 없으면 null (호출부에서 쿼리 파라미터 등으로 폴백).
 *
 * `options.singleton` 이 true 면 sketching 룸 fallback 뿐 아니라 세션의 displayLabel
 * 도 ordinal 을 떼서 반환 (단독 공간 정책).
 */
export function resolveRoomObservingDisplayLabelSpaced(
  session: Pick<RoomObservingSession, 'roomId' | 'displayLabel'>,
  sketchRooms: SketchingSessionRoom[] | null | undefined,
  options?: FormatSketchRoomLabelOptions
): string | null {
  const dl = session.displayLabel?.trim();
  if (dl) return options?.singleton ? stripLeadingOrdinal(dl) : dl;
  const sk = sketchRooms?.find((r) => r.roomId === session.roomId);
  if (sk) return formatSketchingRoomLabelSpaced(sk, options);
  return null;
}

/** 인트로 제목용 접두 — compact 스타일 */
export function formatRoomObservingLeafPrefix(
  session: RoomObservingSession | undefined,
  sketchRooms: SketchingSessionRoom[] | null | undefined,
  fallback: { index: number; namePrefix: string },
  options?: FormatSketchRoomLabelOptions
): string {
  if (!session) {
    return options?.singleton
      ? stripLeadingOrdinal(fallback.namePrefix)
      : `${fallback.index}번${fallback.namePrefix}`;
  }
  const sk = sketchRooms?.find((r) => r.roomId === session.roomId);
  if (sk) return formatSketchingRoomLabelCompact(sk, options);
  const dl = session.displayLabel?.trim();
  if (dl) return options?.singleton ? stripLeadingOrdinal(dl) : dl;
  if (options?.singleton) return stripLeadingOrdinal(fallback.namePrefix);
  if (session.ordinalWithinType != null) {
    return `${session.ordinalWithinType}번${fallback.namePrefix}`;
  }
  return `${fallback.index}번${fallback.namePrefix}`;
}
